import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Offers = lazy(() => import('./pages/Offers'));
const OfferCreate = lazy(() => import('./pages/OfferCreate'));
const OfferEdit = lazy(() => import('./pages/OfferEdit'));
const OfferDetail = lazy(() => import('./pages/OfferDetail'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const PlatformAdmin = lazy(() => import('./pages/PlatformAdmin'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'ADMIN' && user.role !== 'SUPER_ADMIN') return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'SUPER_ADMIN') return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Toaster position="top-right" />
        <BrowserRouter>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/offers" element={<PrivateRoute><Offers /></PrivateRoute>} />
              <Route path="/my-offers" element={<PrivateRoute><Offers myOffers /></PrivateRoute>} />
              <Route path="/offers/new" element={<PrivateRoute><OfferCreate /></PrivateRoute>} />
              <Route path="/offers/:id" element={<PrivateRoute><OfferDetail /></PrivateRoute>} />
              <Route path="/offers/:id/edit" element={<PrivateRoute><OfferEdit /></PrivateRoute>} />
              <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
              <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
              <Route path="/platform-admin" element={<SuperAdminRoute><PlatformAdmin /></SuperAdminRoute>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
